import { Navigate } from "react-router";

import { useFlags } from "launchdarkly-react-client-sdk";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Button } from "primereact/button";
import { Card } from "primereact/card";

import { themeClasses, C } from "sj-ui-toolkit/src/atlas/utils";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useAspenGlobalEnabled } from "sj-ui-toolkit/src/hooks";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";

import {
  useAspenChat,
  ChatInput,
  ChatSessionSidebar,
  ChatSuggestions,
  COMMON_SUGGESTIONS,
  type Suggestion,
} from "sj-ui-toolkit/src/atlas/components/AspenChat";

import { ChatMessageList } from "./ChatMessageList";
import S from "./AspenChatPage.module.scss";
import { isOnlyAiAdvantageBundle } from "sj-ui-toolkit/src/constants";

// tenant admin extends the common suggestions with an assignment-reporting prompt.
const SUGGESTIONS: Suggestion[] = [
  ...COMMON_SUGGESTIONS,
  {
    icon: "pi pi-chart-bar",
    label: "Assignment Reporting",
    prompt:
      "Show me a list of all users who haven't completed {{<assignment name>}}. Provide the email address and statuses for the list of all users.",
    autoSubmit: false,
  },
];

export default function AspenChatPage() {
  const { aspenTenantAdminChat } = useFlags();
  const { isTenantAdmin, contentBundles } = useUserContext();

  const isAiAdvantageOnly = contentBundles === null || isOnlyAiAdvantageBundle(contentBundles);
  const suggestions = SUGGESTIONS.filter(s => !(isAiAdvantageOnly && s.hideForAiAdv));

  if (!aspenTenantAdminChat || !isTenantAdmin) {
    return <Navigate to="/" replace />;
  }

  return <AspenChatPanel suggestions={suggestions} />;
}

function AspenChatPanel({ suggestions }: { suggestions: Suggestion[] }) {
  const {
    selectedTenant,
    sessionId,
    sessions,
    sessionsLoading,
    sessionsError,
    sessionsHasMore,
    transcriptLoading,
    messages,
    input,
    setInput,
    streaming,
    error,
    pendingSelection,
    setPendingSelection,
    hasMessages,
    sendMessage,
    stopStreaming,
    selectSession,
    handleNewChat,
  } = useAspenChat();
  const { aspenGloballyEnabled, isLoading: aspenGlobalSettingLoading } =
    useAspenGlobalEnabled(selectedTenant);
  const aspenDisabled = !aspenGlobalSettingLoading && !aspenGloballyEnabled;
  const chatInputDisabled = aspenGlobalSettingLoading || !aspenGloballyEnabled;

  return (
    <>
      <ConfirmDialog />

      <div className="flex h-[calc(100vh-64px)]">
        <ChatSessionSidebar
          sessions={sessions}
          selectedSessionId={sessionId}
          loading={sessionsLoading}
          error={sessionsError}
          hasMore={sessionsHasMore}
          streaming={streaming}
          onNewChat={handleNewChat}
          onSelectSession={selectSession}
        />

        {!hasMessages ? (
          <div className={S.emptyMessageContainer}>
            {aspenDisabled && <AspenDisabledNotice />}

            <div className="mb-12">
              <h1 className="atlas-h2 mb-4 text-center">Welcome to Aspen</h1>
              <div className="flex justify-center lg:hidden">
                <Button
                  label="New chat"
                  icon="pi pi-plus"
                  size="small"
                  onClick={handleNewChat}
                  disabled={streaming}
                  aria-label="Start new chat"
                />
              </div>
            </div>

            <ChatInput
              input={input}
              streaming={streaming}
              disabled={chatInputDisabled}
              onInput={setInput}
              onSend={sendMessage}
              onStop={stopStreaming}
              pendingSelection={pendingSelection}
              onSelectionApplied={() => setPendingSelection(null)}
            />

            <ChatSuggestions
              suggestions={suggestions}
              tenant={selectedTenant}
              sendMessage={sendMessage}
              setInput={setInput}
              setPendingSelection={setPendingSelection}
              disabled={chatInputDisabled}
            />

            <p className="text-xs text-surface-400 text-center">
              Aspen can make mistakes. Verify important information before acting on it.
            </p>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-w-0">
            <div
              className={C(
                themeClasses.bgElevation1,
                "lg:hidden w-full p-3 flex justify-end border-b border-surface-200"
              )}
            >
              <Button
                label="New chat"
                icon="pi pi-plus"
                size="small"
                onClick={handleNewChat}
                disabled={streaming}
                aria-label="Reset conversation"
              />
            </div>

            <ChatMessageList
              messages={messages}
              transcriptLoading={transcriptLoading}
              error={error}
              streaming={streaming}
            />

            <div className="sticky bottom-0 py-3">
              <div className="w-full max-w-4xl mx-auto px-4">
                {aspenDisabled && <AspenDisabledNotice />}

                <ChatInput
                  input={input}
                  streaming={streaming}
                  disabled={chatInputDisabled}
                  onInput={setInput}
                  onSend={sendMessage}
                  onStop={stopStreaming}
                />
                <p className="text-xs text-surface-400 text-center mt-2">
                  Aspen can make mistakes. Verify important information before acting on it.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function AspenDisabledNotice() {
  const { navigateTo } = useSmartNavigation();
  const settingsRoute = AdminAppRoutes.routes.aspen.routes.settings.route;

  const handleSettingsNavigation = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    navigateTo(settingsRoute);
  };

  return (
    <Card className={S.aspenDisabledNotice}>
      <div className="flex justify-center items-center">
        <i aria-hidden="true" className={C(themeClasses.textWarning, "pi pi-info-circle mr-2")} />
        <p className="text-sm text-center">
          Aspen is currently disabled in your tenant.{" "}
          <a
            href={settingsRoute}
            className="text-primary underline font-semibold"
            onClick={handleSettingsNavigation}
          >
            Enable it here
          </a>{" "}
          to get platform insights and create custom paths.
        </p>
      </div>
    </Card>
  );
}
